import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Palette, Eye, Star, Check } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StickerPreview from '../components/stickers/StickerPreview';
import PrintableStickerPreview from '../components/stickers/PrintableStickerPreview';
import { eventAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';
import { useBadgeSettings } from '../context/BadgeSettingsContext';
import { useEvent } from '../context/EventContext';

const StickerTemplates = () => {
  const toast = useToastContext();
  const {
    defaultTemplate,
    showQR,
    stickerSize,
    stickerWidth,
    stickerHeight,
    useCustomStickerSize,
    updateDefaultTemplate,
    updateShowQR,
    updateStickerSize,
    updateStickerWidth,
    updateStickerHeight,
    updateUseCustomStickerSize,
    loadEventSettings,
    // New customization settings
    stickerNameFontSize,
    stickerPositionFontSize,
    stickerOrgFontSize,
    stickerLineHeight,
    stickerLogoSize,
    stickerShowLogo,
    stickerAutoScaleFont,
    qrPosition,
    stickerNameFontWeight,
    stickerPositionFontWeight,
    stickerOrgFontWeight,
    stickerShowPosition,
    stickerShowOrganization,
    updateStickerNameFontSize,
    updateStickerPositionFontSize,
    updateStickerOrgFontSize,
    updateStickerLineHeight,
    updateStickerLogoSize,
    updateStickerShowLogo,
    updateStickerAutoScaleFont,
    updateQrPosition,
    updateStickerNameFontWeight,
    updateStickerPositionFontWeight,
    updateStickerOrgFontWeight,
    updateStickerShowPosition,
    updateStickerShowOrganization
  } = useBadgeSettings();
  const { selectedEvent, setSelectedEvent } = useEvent();

  const [selectedLayout, setSelectedLayout] = useState('classic');
  const [previewSize, setPreviewSize] = useState(stickerSize);

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ['events'],
    queryFn: async () => (await eventAPI.getAll()).data,
  });

  // Auto-select first event
  React.useEffect(() => {
    const events = eventsData?.events || [];
    if (events.length > 0 && !selectedEvent) {
      setSelectedEvent(events[0].id);
    }
  }, [eventsData, selectedEvent, setSelectedEvent]);

  // Load event settings when event changes
  React.useEffect(() => {
    if (selectedEvent) {
      loadEventSettings(selectedEvent);
    }
  }, [selectedEvent, loadEventSettings]);

  // Sticker layout options
  const stickerLayouts = [
    {
      id: 'classic',
      name: 'Classic Purple',
      description: 'Traditional purple design like your reference',
      background: 'from-purple-500 to-purple-600'
    },
    {
      id: 'corporate',
      name: 'Corporate Blue',
      description: 'Professional blue theme',
      background: 'from-blue-600 to-blue-700'
    },
    {
      id: 'modern',
      name: 'Modern Dark',
      description: 'Sleek dark theme',
      background: 'from-gray-800 to-gray-900'
    },
    {
      id: 'minimal',
      name: 'Minimal White',
      description: 'Clean white background',
      background: 'from-white to-gray-50'
    },
    {
      id: 'tech',
      name: 'Tech Green',
      description: 'Technology-focused green theme',
      background: 'from-green-600 to-teal-600'
    },
    {
      id: 'elegant',
      name: 'Elegant Indigo',
      description: 'Sophisticated indigo gradient',
      background: 'from-indigo-600 to-purple-600'
    }
  ];

  // Sample data for preview
  const sampleData = {
    name: 'Albert Kwame Tchalla',
    position: 'Software Engineer',
    organization: 'KNUST',
    title: 'Software Engineer', // Alternative field name
    company: 'KNUST', // Alternative field name
    attendeeId: '12345',
    eventId: selectedEvent
  };


  const handleSetDefault = (layoutId) => {
    updateDefaultTemplate(layoutId);
    setSelectedLayout(layoutId);
    toast.success(`${stickerLayouts.find(l => l.id === layoutId)?.name} set as default!`);
  };

  const events = eventsData?.events || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Sticker Templates</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Design simple, scannable stickers for attendees</p>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm w-full sm:w-auto"
            >
              <option value="">Select Event</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Settings Section */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column - Preview Settings */}
          <Card className="xl:col-span-1">
            <Card.Content>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview Settings</h3>

              <div className="space-y-4">
                {/* Size Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                  <select
                    value={previewSize}
                    onChange={(e) => {
                      setPreviewSize(e.target.value);
                      updateStickerSize(e.target.value);
                    }}
                    disabled={useCustomStickerSize}
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 w-full"
                  >
                    <option value="tiny">Tiny (1" × 1")</option>
                    <option value="small">Small (1.5" × 1")</option>
                    <option value="40x20mm">40mm × 20mm</option>
                    <option value="40x30mm">40mm × 30mm</option>
                    <option value="normal">Normal (2" × 1.5")</option>
                    <option value="medium">Medium (2.5" × 1.5")</option>
                    <option value="60x40mm">60mm × 40mm</option>
                    <option value="large">Large (3" × 2")</option>
                    <option value="80x50mm">80mm × 50mm</option>
                  </select>
                </div>

                {/* Width Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Width (inches)</label>
                  <input
                    type="number"
                    value={(stickerWidth / 96).toFixed(2)}
                    onChange={(e) => updateStickerWidth(Math.round((parseFloat(e.target.value) || 2.33) * 96))}
                    min="1"
                    max="5"
                    step="0.25"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  />
                  <div className="text-xs text-gray-500 mt-1">{stickerWidth}px</div>
                </div>

                {/* Height Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Height (inches)</label>
                  <input
                    type="number"
                    value={(stickerHeight / 96).toFixed(2)}
                    onChange={(e) => updateStickerHeight(Math.round((parseFloat(e.target.value) || 1.83) * 96))}
                    min="0.5"
                    max="4"
                    step="0.25"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  />
                  <div className="text-xs text-gray-500 mt-1">{stickerHeight}px</div>
                </div>

                {/* Checkboxes */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={useCustomStickerSize}
                      onChange={(e) => updateUseCustomStickerSize(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Custom Size</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={showQR}
                      onChange={(e) => updateShowQR(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Show QR Code</span>
                  </label>
                </div>

                {/* QR Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">QR Code Position</label>
                  <select
                    value={qrPosition}
                    onChange={(e) => updateQrPosition(e.target.value)}
                    disabled={!showQR}
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 w-full"
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            </Card.Content>
          </Card>

          {/* Right Column - Sticker Customization */}
          <Card className="xl:col-span-2">
            <Card.Content>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Sticker Customization</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Auto-scale font */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={stickerAutoScaleFont}
                      onChange={(e) => updateStickerAutoScaleFont(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Auto-scale Font</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Reduce font for long text</p>
                </div>

                {/* Name font size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name Font Size (px)</label>
                  <input
                    type="number"
                    value={stickerNameFontSize}
                    onChange={(e) => updateStickerNameFontSize(parseInt(e.target.value) || 14)}
                    min="8"
                    max="24"
                    step="1"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  />
                </div>

                {/* Name font weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name Font Weight</label>
                  <select
                    value={stickerNameFontWeight}
                    onChange={(e) => updateStickerNameFontWeight(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  >
                    <option value="normal">Normal (400)</option>
                    <option value="medium">Medium (500)</option>
                    <option value="semibold">Semi-Bold (600)</option>
                    <option value="bold">Bold (700)</option>
                    <option value="extrabold">Extra Bold (800)</option>
                  </select>
                </div>

                {/* Position font size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position Font Size (px)</label>
                  <input
                    type="number"
                    value={stickerPositionFontSize}
                    onChange={(e) => updateStickerPositionFontSize(parseInt(e.target.value) || 11)}
                    min="6"
                    max="18"
                    step="1"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  />
                </div>

                {/* Position font weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position Font Weight</label>
                  <select
                    value={stickerPositionFontWeight}
                    onChange={(e) => updateStickerPositionFontWeight(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  >
                    <option value="normal">Normal (400)</option>
                    <option value="medium">Medium (500)</option>
                    <option value="semibold">Semi-Bold (600)</option>
                    <option value="bold">Bold (700)</option>
                    <option value="extrabold">Extra Bold (800)</option>
                  </select>
                </div>

                {/* Organization font size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Font Size (px)</label>
                  <input
                    type="number"
                    value={stickerOrgFontSize}
                    onChange={(e) => updateStickerOrgFontSize(parseInt(e.target.value) || 11)}
                    min="6"
                    max="18"
                    step="1"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  />
                </div>

                {/* Organization font weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Font Weight</label>
                  <select
                    value={stickerOrgFontWeight}
                    onChange={(e) => updateStickerOrgFontWeight(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  >
                    <option value="normal">Normal (400)</option>
                    <option value="medium">Medium (500)</option>
                    <option value="semibold">Semi-Bold (600)</option>
                    <option value="bold">Bold (700)</option>
                    <option value="extrabold">Extra Bold (800)</option>
                  </select>
                </div>

                {/* Line height */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Line Height</label>
                  <input
                    type="number"
                    value={stickerLineHeight}
                    onChange={(e) => updateStickerLineHeight(parseFloat(e.target.value) || 1.2)}
                    min="0.8"
                    max="2.0"
                    step="0.1"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Controls spacing between lines</p>
                </div>

                {/* Show logo */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={stickerShowLogo}
                      onChange={(e) => updateStickerShowLogo(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Show Logo</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Display event logo</p>
                </div>

                {/* Show Position */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={stickerShowPosition}
                      onChange={(e) => updateStickerShowPosition(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Show Position</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Display job title/role</p>
                </div>

                {/* Show Organization */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={stickerShowOrganization}
                      onChange={(e) => updateStickerShowOrganization(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Show Organization</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Display company/org</p>
                </div>

                {/* Logo size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo Size (px)</label>
                  <input
                    type="number"
                    value={stickerLogoSize}
                    onChange={(e) => updateStickerLogoSize(parseInt(e.target.value) || 40)}
                    min="20"
                    max="80"
                    step="5"
                    disabled={!stickerShowLogo}
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 w-full"
                  />
                </div>
              </div>
            </Card.Content>
          </Card>
        </div>

        {/* Live Preview */}
        < Card >
          <Card.Content>
            <div className="flex flex-col items-center">
              <p className="text-sm text-gray-500 mb-2">Print Preview</p>
              <div className="w-full flex justify-center">
                <div className="w-full max-w-md">
                  <PrintableStickerPreview
                    layout={selectedLayout}
                    data={sampleData}
                    showQR={showQR}
                    qrPosition={qrPosition}
                    size={previewSize}
                    customWidth={useCustomStickerSize ? stickerWidth : null}
                    customHeight={useCustomStickerSize ? stickerHeight : null}
                    showPaper={true}
                    paperSize="letter"
                    nameFontSize={stickerNameFontSize}
                    positionFontSize={stickerPositionFontSize}
                    orgFontSize={stickerOrgFontSize}
                    lineHeight={stickerLineHeight}
                    logoSize={stickerLogoSize}
                    showLogo={stickerShowLogo}
                    autoScaleFont={stickerAutoScaleFont}
                    nameFontWeight={stickerNameFontWeight}
                    positionFontWeight={stickerPositionFontWeight}
                    orgFontWeight={stickerOrgFontWeight}
                    showPosition={stickerShowPosition}
                    showOrganization={stickerShowOrganization}
                  />
                </div>
              </div>
            </div>
          </Card.Content>
        </Card >

        {/* Sticker Layouts Grid */}
        < div >
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Layouts</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {stickerLayouts.map((layout) => (
              <Card key={layout.id} className={selectedLayout === layout.id ? 'ring-2 ring-primary-500' : ''}>
                <Card.Content>
                  <div className="space-y-4">
                    {/* Layout Preview */}
                    <div className="flex justify-center">
                      <StickerPreview
                        layout={layout.id}
                        data={sampleData}
                        showQR={showQR}
                        qrPosition={qrPosition}
                        size="small"
                        customWidth={144}
                        customHeight={96}
                        nameFontSize={stickerNameFontSize}
                        positionFontSize={stickerPositionFontSize}
                        orgFontSize={stickerOrgFontSize}
                        lineHeight={stickerLineHeight}
                        logoSize={stickerLogoSize}
                        showLogo={stickerShowLogo}
                        autoScaleFont={stickerAutoScaleFont}
                        nameFontWeight={stickerNameFontWeight}
                        positionFontWeight={stickerPositionFontWeight}
                        orgFontWeight={stickerOrgFontWeight}
                        showPosition={stickerShowPosition}
                        showOrganization={stickerShowOrganization}
                      />
                    </div>

                    {/* Layout Info */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{layout.name}</h3>
                        {defaultTemplate === layout.id && (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check className="h-4 w-4" />
                            <span className="text-xs font-medium">Default</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{layout.description}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSelectedLayout(layout.id)}
                        className="flex-1 gap-2 text-xs sm:text-sm justify-center"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Preview</span>
                      </Button>
                      <Button
                        variant={defaultTemplate === layout.id ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => handleSetDefault(layout.id)}
                        className="flex-1 gap-2 text-xs sm:text-sm justify-center"
                      >
                        <Star className="h-4 w-4" />
                        <span>{defaultTemplate === layout.id ? 'Default' : 'Set Default'}</span>
                      </Button>
                    </div>
                  </div>
                </Card.Content>
              </Card>
            ))}
          </div>
        </div >

        {/* Sticker Information */}
        < Card >
          <Card.Content>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Palette className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">About Sticker Templates</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">What's Included</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>QR Code</strong> - Links to public attendee detail page (no login needed)</li>
                    <li>• <strong>Name</strong> - Bold, prominent display</li>
                    <li>• <strong>Position</strong> - Job title or role</li>
                    <li>• <strong>Organization</strong> - Company or institution</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Sticker Benefits</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Compact Size</strong> - Perfect for sticker printing</li>
                    <li>• <strong>Cost Effective</strong> - Smaller than full badges</li>
                    <li>• <strong>Easy Scanning</strong> - QR codes for instant digital details</li>
                    <li>• <strong>Open Access</strong> - No login required for viewing profiles</li>
                    <li>• <strong>Professional Look</strong> - Clean, modern designs</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Printing Features</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Multiple Sizes</strong> - From 1"×1" to 3"×2"</li>
                    <li>• <strong>Custom Dimensions</strong> - Set your own width and height</li>
                    <li>• <strong>Font Customization</strong> - Adjust all text sizes</li>
                    <li>• <strong>Auto-scaling</strong> - Fits long names automatically</li>
                    <li>• <strong>Logo Support</strong> - Add and resize event logo</li>
                  </ul>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Open Access:</strong> When someone scans the QR code, they'll be taken to a detailed attendee profile page
                  with contact information and event details. <strong>No login required</strong> - perfect for networking!
                </p>
              </div>
            </div>
          </Card.Content>
        </Card >
      </div >
    </Layout >
  );
};

export default StickerTemplates;
